import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { AIContextKeyType } from "components/ai/hooks/use-ai-context";
import type { ToolNameType } from "components/ai/tools/types";
import { AIModelType, AIServerFileType, AIServerRoleType } from "components/server-types";
import { createReplaceAllReducer } from "store/utils";

/**
 * Changing this key (e.g. by bumping the version) will clear everyone's
 * AI-related state on next refresh / deploy.
 * Since AI messages aren't critical, we can use this to reset state
 * when we make changes to the implementation that would otherwise require
 * a more complex migration.
 */
export const LOCAL_AI_STATE_KEY = "monarch-local-ai-v3";

export type AIMessageBase = {
  id: string;
  role: AIServerRoleType;
  text: string;
  createdAt: string; // ISO datetime string
  finishedAt?: string; // ISO datetime string
  isLoading?: boolean;
};

export type AIContextItem = {
  id: string;
  text: string;
  type: "context";
  role: AIServerRoleType;
};

export type AITextMessage = AIMessageBase & {
  type: "message";
};

// TODO: Maybe this shouldn't inherit "text" from the base message type
export type AIToolMessage = AIMessageBase & {
  type: "function_call";
  name: ToolNameType;
  arguments: Record<string, unknown>;
  call_id: string;
  status?: string;
};

export type AIToolResponseMessage = Omit<AIMessageBase, "text"> & {
  type: "function_call_output";
  call_id: string;
  output: unknown;
  error?: string;
};

export type AIChatMessage = AITextMessage | AIToolMessage | AIToolResponseMessage;

export type AIFileType = AIServerFileType & {
  source?: string;
  error?: string;
};

export type AIState = {
  aiChatMessages: Record<string, AIChatMessage>;
  /**
   * Context items sent alongside chat history. Stored separately from chat messages
   * and indexed by a stable id provided by callers of setAiChatData.
   */
  contextItems: Partial<Record<AIContextKeyType, AIContextItem>>;
  /**
   * Files available in the current context, sent alongside chat history,
   * and available for AI actions and tools.
   */
  contextFiles: Record<string, AIFileType>;
  /**
   * Tools currently available to the AI.
   * Actual tool definitions are in src/components/ai/tools,
   * these are just references.
   */
  tools: ToolNameType[];

  /* whether on not we allow AI auto-assess submissions */
  assessWithAIMode: boolean;
  /* whether or not we allow ai to see student responses when viewing page content on chat panel */
  isStudentResponsesForAIEnabled: boolean;
  aiDebuggerVisible: boolean;
  userSelectedAIModel: AIModelType | null;
};

const defaultState: AIState = {
  aiChatMessages: {},
  contextItems: {},
  tools: [],
  contextFiles: {},
  assessWithAIMode: false,
  isStudentResponsesForAIEnabled: false,
  aiDebuggerVisible: false,
  userSelectedAIModel: null,
};

const retrieveLocalAiState = () => {
  const data = localStorage.getItem(LOCAL_AI_STATE_KEY);
  if (!data) return defaultState;
  const storedState = JSON.parse(data) as Partial<AIState>;

  return {
    ...defaultState,
    ...storedState,
    // We don't save these in local state, since they're managed by hooks
    contextItems: {},
    tools: [],
    contextFiles: {},
  };
};

const saveLocalState = (state: AIState) => {
  localStorage.setItem(LOCAL_AI_STATE_KEY, JSON.stringify(state));
};

const deleteLocalState = () => {
  localStorage.removeItem(LOCAL_AI_STATE_KEY);
};

const initialState = retrieveLocalAiState;

export const AIStore = createSlice({
  name: "ai",
  initialState,
  reducers: {
    setAssessWithAIMode: (state, action: PayloadAction<boolean>) => {
      state.assessWithAIMode = action.payload;
      saveLocalState(state);
    },
    addTool: (state, action: PayloadAction<ToolNameType>) => {
      if (!state.tools.includes(action.payload)) {
        state.tools.push(action.payload);
        saveLocalState(state);
      }
    },
    removeTool: (state, action: PayloadAction<ToolNameType>) => {
      state.tools = state.tools.filter((tool) => tool !== action.payload);
      saveLocalState(state);
    },
    setAiChatData: (state, action: PayloadAction<AIContextItem>) => {
      if (!state.contextItems) state.contextItems = {};
      state.contextItems[action.payload.id] = { ...action.payload };
    },
    removeAiChatData: (state, action: PayloadAction<AIContextKeyType>) => {
      if (state.contextItems) {
        delete state.contextItems[action.payload];
      }
    },
    setContextFile: (state, action: PayloadAction<AIFileType>) => {
      state.contextFiles[action.payload.url] = { ...action.payload };
      saveLocalState(state);
    },
    removeContextFile: (state, action: PayloadAction<AIFileType>) => {
      delete state.contextFiles[action.payload.url];
    },
    setAiChatMessage: (state, action: PayloadAction<AIChatMessage>) => {
      if (!state.aiChatMessages) state.aiChatMessages = {};
      state.aiChatMessages[action.payload.id] = { ...action.payload };
      // Uncomment for debugging:
      // console.info(`setAiChatMessage id=${action.payload.id}:`, state.aiChatMessages[action.payload.id]);
      saveLocalState(state);
    },
    deleteAiChatMessage: (state, action: PayloadAction<string>) => {
      if (!state.aiChatMessages) state.aiChatMessages = {};
      delete state.aiChatMessages[action.payload];
      saveLocalState(state);
    },
    clearAiChatMessages: (state) => {
      state.aiChatMessages = {};
      saveLocalState(state);
    },
    setStudentResponsesForAIEnabled: (state, action: PayloadAction<boolean>) => {
      state.isStudentResponsesForAIEnabled = action.payload;
      saveLocalState(state);
    },
    setAiDebuggerVisible: (state, action: PayloadAction<boolean>) => {
      state.aiDebuggerVisible = action.payload;
      saveLocalState(state);
    },
    setUserSelectedAIModel: (state, action: PayloadAction<AIModelType | null>) => {
      state.userSelectedAIModel = action.payload;
      saveLocalState(state);
    },
  },
  extraReducers: createReplaceAllReducer<AIState>("ai", initialState, () => {
    deleteLocalState();
  }),
});

export const aiStateActions = AIStore.actions;
